<template>
  <div>
    <ViewLoadingMessage :loading="loading.orderDetail || prefetchLoading"/>

    <!-- Prefetch Loading -->
    <div v-if="!loading.orderDetail && prefetchLoading" class="text-center py-5">
      <CSpinner color="primary" class="mb-3"/>
      <p>
        <translate translate-context="edge-orders.detail">Loading media information...</translate>
      </p>
    </div>

    <template v-if="currentOrder && !loading.orderDetail && !prefetchLoading">
      <!-- Order Summary -->
      <OrderSummary
        :order="currentOrder"
      />

      <!-- Order Items Table (Financial Overview) -->
      <OrderItemsTable
        :order-items="groupedOrderItems"
        :order="currentOrder"
        :get-media-count="getMediaCount"
      />

      <!-- Payment Details -->
      <PaymentDetails :order="currentOrder" class="mb-4" />

      <!-- Order Items Media (Photos Section) -->
      <div v-if="groupedOrderItems.length > 0" class="mb-3">
        <div class="mb-3">
          <h5 class="mb-0">
            <CIcon name="cipImage" class="mr-2"/>
            <translate translate-context="edge-orders.detail">Photos</translate>
          </h5>
        </div>

        <!-- Bundles -->
        <template v-for="item in groupedOrderItems">
          <BundleItem
            v-if="item.isBundleGroup"
            :key="'bundle-' + item.bundle.id"
            :bundle-group="item"
            :order="currentOrder"
          >
            <template #default="{ items }">
              <OrderItem
                v-for="bundleItem in items"
                :key="bundleItem.id"
                :item="bundleItem"
                :order="currentOrder"
                @zoom="showMediaZoom"
              />
            </template>
          </BundleItem>

          <!-- Non-bundle items -->
          <OrderItem
            v-else
            :key="'item-' + item.id"
            :item="item"
            :order="currentOrder"
            @zoom="showMediaZoom"
          />
        </template>
      </div>

    </template>

    <!-- Media Zoom Modal -->
    <MediaZoomModal
      ref="mediaZoomModal"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ViewLoadingMessage from '@/components/ViewLoadingMessage.vue'
import OrderSummary from './OrderSummary.vue'
import OrderItem from './OrderItem.vue'
import BundleItem from '../shared/BundleItem.vue'
import OrderItemsTable from '../shared/OrderItemsTable.vue'
import MediaZoomModal from '../shared/MediaZoomModal.vue'
import PaymentDetails from './PaymentDetails.vue'
import { getOrderStatusLabel, getOrderStatusColor } from '@/domain/core/constant/orderStatus'
import Filters from '@/domain/core/utils/filters'

export default {
  name: 'EdgeOrderDetail',
  components: {
    ViewLoadingMessage,
    OrderSummary,
    OrderItem,
    BundleItem,
    OrderItemsTable,
    MediaZoomModal,
    PaymentDetails
  },
  data() {
    return {
      prefetchLoading: false
    }
  },
  computed: {
    ...mapGetters('edgeOrders', [
      'currentOrder',
      'orderItems',
      'groupedOrderItems',
      'loading',
      'picaServiceId',
      'getMediaForOrderItem'
    ])
  },
  methods: {
    ...mapActions('edgeOrders', [
      'fetchOrderDetail',
      'clearCurrentOrder',
      'prefetchAllOrderMedia'
    ]),
    ...mapActions('login', [
      'fetchConfig'
    ]),

    getMediaCount(item) {
      const mediaData = this.getMediaForOrderItem(item.id)
      return mediaData.media?.length || '-'
    },

    async loadOrderDetail() {
      const orderId = this.$route.params.orderId
      try {
        // Load order details
        await this.fetchOrderDetail(orderId)

        // Prefetch all media for items with media
        const itemsWithMedia = this.orderItems.filter(item =>
          (item.media_ids && item.media_ids.length > 0) ||
          (item.gallery_ids && item.gallery_ids.length > 0)
        )

        if (itemsWithMedia.length > 0) {
          this.prefetchLoading = true

          try {
            const result = await this.prefetchAllOrderMedia()

            if (!result.success) {
              console.warn('Some media failed to load:', result)
            }

            // Force OrderSummary to recompute after media is loaded
            await this.$nextTick()
            this.$forceUpdate()
          } catch (error) {
            console.error('Failed to prefetch media:', error)
            // Continue anyway - the app will still work with lazy loading
          } finally {
            this.prefetchLoading = false
          }
        }
      } catch (error) {
        console.error('Failed to load order details:', error)
        this.notifyError(this.$pgettext('edge-orders.detail', 'Failed to load order details'))
        this.$router.push({ name: 'edgeOrderList', params: { picaServiceId: this.picaServiceId } })
      }
    },
    getStatusLabel(status) {
      return getOrderStatusLabel(status)
    },
    getStatusColor(status) {
      return getOrderStatusColor(status)
    },
    getStatusBadgeColor(status) {
      const color = this.getStatusColor(status)
      // Map to lighter badge colors
      const badgeColorMap = {
        'primary': 'light',
        'success': 'light',
        'warning': 'light',
        'danger': 'light',
        'secondary': 'light',
        'dark': 'light'
      }
      return badgeColorMap[color] || 'light'
    },

    showMediaZoom(media) {
      this.$refs.mediaZoomModal.open(media)
    }
  },
  async mounted() {
    // Fetch edge config for permissions
    const picaServiceId = this.$route.params.picaServiceId
    try {
      await this.fetchConfig({
        type: 'edge_orders',
        id: picaServiceId
      })
    } catch (error) {
      console.error('Failed to fetch edge config:', error)
    }

    await this.loadOrderDetail()
  },
  beforeDestroy() {
    this.clearCurrentOrder()
  },
  filters: {
    currency: Filters.currency,
    formatDate: Filters.formatDate
  }
}
</script>

<style scoped>
.main-header {
  font-size: 1.75rem;
  font-weight: 600;
}

.table-compact td,
.table-compact th {
  padding: 0.5rem 0.75rem;
}

.list-striped dt,
.list-striped dd {
  padding: 0.75rem 1rem;
}

.list-striped dt:nth-of-type(odd),
.list-striped dd:nth-of-type(odd) {
  background-color: #f8f9fa;
}

.custom-table {
  font-size: 0.875rem;
}

.font-weight-medium {
  font-weight: 500;
}

.print-job-item:last-child {
  border-bottom: none !important;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}
</style>